import {useAuth} from "oidc-react";
import {Button, Card, Form, Input, Space, Typography} from "antd";
import {ReactElement, Suspense, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {ApplicationUserRole} from "../../api";
import useCurrentUserRoles from "../../hooks/useCurrentUserRoles";
import {getCurrentUser} from "../../redux/applicationUserSlice";
import {loadingSpin, RoutingPaths} from "./routingContainer";
import styles from "./routingWrappers.module.less";

const appAccessPassword = "PiknikCNK2026";
const appAccessStorageKey = "cnk-map-app-access";
const appAccessPath = "/test-access";
const isDevelopmentEnvironment = process.env.NODE_ENV === "development";

let hasInMemoryAccess = false;

function getHasStoredAccess() {
    if (typeof window === "undefined") {
        return hasInMemoryAccess;
    }

    return hasInMemoryAccess
        || localStorage.getItem(appAccessStorageKey) === "granted"
        || sessionStorage.getItem(appAccessStorageKey) === "granted";
}

function grantAccess() {
    hasInMemoryAccess = true;

    if (typeof window !== "undefined") {
        localStorage.setItem(appAccessStorageKey, "granted");
        sessionStorage.setItem(appAccessStorageKey, "granted");
    }
}

export function AppAccessGate(props: { children: ReactElement }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [hasAccess, setHasAccess] = useState<boolean>(() => getHasStoredAccess());
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const [form] = Form.useForm<{ password: string }>();
    const isUnlocked = true;//useMemo(() => hasAccess || getHasStoredAccess(), [hasAccess]);
    const isAccessPath = location.pathname === appAccessPath;

    if (isDevelopmentEnvironment) {
        return props.children;
    }

    const handleSubmit = ({password}: { password: string }) => {
        if (password === appAccessPassword) {
            grantAccess();
            setHasAccess(true);
            setIsInvalid(false);
            navigate("/", {replace: true});
            return;
        }

        setIsInvalid(true);
        form.setFields([{name: "password", errors: ["Podane hasło jest nieprawidłowe."]}]);
    };

    const handleChange = () => {
        if (!isInvalid) {
            return;
        }

        setIsInvalid(false);
        form.setFields([{name: "password", errors: []}]);
    };

    if (isUnlocked) {
        return props.children;
    }

    if (!isAccessPath) {
        return <div className={styles.accessGate}>
            <div className={styles.backgroundGlow}/>
            <Card bordered={false} className={styles.accessCard}>
                <Space direction="vertical" size={10} className={styles.accessContent}>
                    <Typography.Title level={2} className={styles.title}>
                        Już wkrótce pojawi się tutaj tegoroczna edycja interaktywnej mapy 29. Pikniku Naukowego
                    </Typography.Title>
                </Space>
            </Card>
        </div>;
    }

    return <div className={styles.accessGate}>
        <div className={styles.backgroundGlow}/>
        <Card bordered={false} className={styles.accessCard}>
            <Space direction="vertical" size={10} className={styles.accessContent}>
                <Typography.Title level={2} className={styles.title}>Dostęp do aplikacji</Typography.Title>
                <Typography.Paragraph className={styles.description}>
                    Wpisz hasło, aby odblokować dostęp do testowej wersji mapy.
                </Typography.Paragraph>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    autoComplete="off"
                    className={styles.form}
                >
                    <Form.Item
                        label="Hasło"
                        name="password"
                        validateStatus={isInvalid ? "error" : undefined}
                        rules={[{required: true, message: "Wpisz hasło."}]}
                    >
                        <Input.Password
                            className={styles.passwordInput}
                            size="large"
                            placeholder="Wpisz hasło"
                            onChange={handleChange}
                            onPressEnter={() => form.submit()}
                        />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" size="large" className={styles.submitButton}>
                        Wejdź do aplikacji
                    </Button>
                </Form>
            </Space>
        </Card>
    </div>;
}

export function AuthInitialized(props: { children: ReactElement, loadingIndicator: ReactElement }) {
    const [isTryingToLogin, setIsTryingToLogin] = useState<boolean>(false);
    const auth = useAuth();

    useEffect(() => {
        if (!auth.isLoading && !auth.userData) {
            setIsTryingToLogin(true);

            auth.userManager
                .signinSilent()
                .catch((error) => {
                    console.log("Authentication error:", error.message);
                    return null;
                })
                .finally(() => setIsTryingToLogin(false));
        }
    }, [auth.isLoading, auth.userData, auth.userManager]);

    if (auth.isLoading) {
        return props.loadingIndicator || null;
    }

    if (isTryingToLogin) {
        return props.loadingIndicator || null;
    }

    return <Suspense fallback={loadingSpin}>
        {props.children}
    </Suspense>;
}

export function Protected(props: { children: ReactElement, loadingIndicator: ReactElement, allowedForRoles?: ApplicationUserRole[] }) {
    const auth = useAuth();
    const navigate = useNavigate();
    const user = useSelector(getCurrentUser);
    const roles = useCurrentUserRoles();
    const location = useLocation();


    useEffect(() => {
        if (props.allowedForRoles && user && !roles.some(r => props.allowedForRoles!.includes(r))) {
            navigate(RoutingPaths.mainPage.route, {replace: true});
        }
    }, [user, roles, props.allowedForRoles, navigate]);

    if (auth.isLoading) {
        return props.loadingIndicator;
    }

    if (!auth.userData) {
        auth.userManager.signinSilent().catch(() => auth.signIn({state: {targetPath: location.pathname}}));

        return props.loadingIndicator;
    }

    return <Suspense fallback={loadingSpin}>
        {props.children}
    </Suspense>;
}
